import React from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  FloatButton,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { CopyOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, GlobalOutlined, LinkOutlined, PlusOutlined, ReloadOutlined, RobotOutlined } from "@ant-design/icons";
import PostEditorDrawer from "./components/PostEditorDrawer";
import BulkAIPostsDrawer from "./components/BulkAIPostsDrawer";

const { TextArea } = Input;
const { Title, Text } = Typography;

const statusColor = {
  draft: "default",
  scheduled: "gold",
  published: "green",
  archived: "volcano",
};

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Scheduled", value: "scheduled" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("vi-VN");
  } catch {
    return "-";
  }
};

const Desktop = ({ viewModel, isMobile = false }) => {
  const {
    loading,
    posts,
    totalPosts,
    postPaging,
    postFilters,
    selectedPostIDs,
    setSelectedPostIDs,
    bulkPostStatus,
    setBulkPostStatus,
    bulkUpdatingStatus,
    categories,
    tags,
    categoryOptions,
    tagOptions,
    editorOpen,
    editingPost,
    editorLoading,
    revisions,
    revisionLoading,
    aiGenerating,
    aiFacebookGenerating,
    aiFacebookContent,
    setAIFacebookContent,
    aiImageGenerating,
    aiImagePrompt,
    setAIImagePrompt,
    aiGeneratedImage,
    setAIGeneratedImage,
    bulkAIOpen,
    bulkAITopic,
    setBulkAITopic,
    bulkAICount,
    setBulkAICount,
    bulkAITopics,
    bulkAISelectedIDs,
    setBulkAISelectedIDs,
    bulkAIGeneratingTopics,
    bulkAICreatingPosts,
    pingingGoogleSitemap,
    postForm,
    createPost,
    editPost,
    closeEditor,
    savePost,
    deletePost,
    restoreRevision,
    onEditorStatusChange,
    onPostSearch,
    onPostPageChange,
    updateSelectedPostsStatus,
    checkSlug,
    categoryName,
    setCategoryName,
    saveCategory,
    removeCategory,
    savingCategory,
    tagName,
    setTagName,
    saveTag,
    removeTag,
    savingTag,
    brandForm,
    brandLoading,
    brandSaving,
    saveBrandProfile,
    openWebsite,
    openPostPreview,
    generatePostHTMLByAI,
    generateFacebookPostByAI,
    copyFacebookPostContent,
    generatePostImageByAI,
    applyGeneratedImageAsFeatured,
    appendGeneratedImageToGallery,
    openBulkAIDrawer,
    closeBulkAIDrawer,
    generateTopicListByAI,
    createSelectedPostsByAI,
    openSitemap,
    copySitemapLink,
    downloadSitemap,
    pingGoogleSitemap,
    reload,
  } = viewModel;

  const postColumns = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      render: (v, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || "-"}</Text>
          <Text type="secondary">/{record.slug}</Text>
        </Space>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 120,
      render: (v) => <Tag color={statusColor[v] || "default"}>{v || "-"}</Tag>,
    },
    {
      title: "Lịch đăng",
      dataIndex: "publish_at",
      width: 170,
      render: (v) => formatDate(v),
    },
    {
      title: "Cập nhật",
      dataIndex: "update_on",
      width: 170,
      render: (v) => formatDate(v),
    },
    {
      title: "Thao tác",
      width: 260,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openPostPreview(record)}>
            Xem
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => editPost(record)}>
            Sửa
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deletePost(record)}>
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  const renderMobilePostCard = (record) => {
    const isChecked = selectedPostIDs.includes(record.id);
    return (
      <Card key={record.id} size="small" style={{ borderRadius: 12 }}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Checkbox checked={isChecked} onChange={(e) => {
            const checked = e?.target?.checked;
            setSelectedPostIDs((prev) => {
              if (checked) {
                return Array.from(new Set([...(prev || []), record.id]));
              }
              return (prev || []).filter((id) => id !== record.id);
            });
          }}>
            Chọn bài viết này
          </Checkbox>

          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Text strong>{record?.title || "-"}</Text>
            <Text type="secondary">/{record?.slug || ""}</Text>
          </Space>

          <Space wrap>
            <Tag color={statusColor[record?.status] || "default"}>{record?.status || "-"}</Tag>
            <Text type="secondary">Đăng: {formatDate(record?.publish_at)}</Text>
          </Space>

          <Text type="secondary">Cập nhật: {formatDate(record?.update_on)}</Text>

          <Space style={{ width: "100%" }}>
            <Button block icon={<EyeOutlined />} onClick={() => openPostPreview(record)}>
              Xem
            </Button>
            <Button block icon={<EditOutlined />} onClick={() => editPost(record)}>
              Sửa
            </Button>
            <Button block danger icon={<DeleteOutlined />} onClick={() => deletePost(record)}>
              Xóa
            </Button>
          </Space>
        </Space>
      </Card>
    );
  };

  const categoryColumns = [
    { title: "Tên danh mục", dataIndex: "name" },
    {
      title: "Thao tác",
      width: 120,
      render: (_, record) => (
        <Popconfirm title="Xóa danh mục?" onConfirm={() => removeCategory(record)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const tagColumns = [
    { title: "Tag", dataIndex: "name" },
    {
      title: "Thao tác",
      width: 120,
      render: (_, record) => (
        <Popconfirm title="Xóa thẻ?" onConfirm={() => removeTag(record)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {isMobile ? (
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Title level={4} style={{ margin: 0 }}>Quản lý nội dung và SEO</Title>

          <Space wrap style={{ width: "100%" }}>
            <Button icon={<GlobalOutlined />} onClick={openWebsite}>Website</Button>
            <Button icon={<LinkOutlined />} onClick={openSitemap}>Sitemap</Button>
            <Button icon={<CopyOutlined />} onClick={copySitemapLink}>Copy sitemap</Button>
            <Button icon={<DownloadOutlined />} onClick={downloadSitemap}>Tải sitemap</Button>
            <Button loading={pingingGoogleSitemap} onClick={pingGoogleSitemap}>Ping Google</Button>
            <Button icon={<ReloadOutlined />} onClick={reload}>Làm mới</Button>
            <Button icon={<RobotOutlined />} onClick={openBulkAIDrawer}>Tạo bài AI</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={createPost}>Tạo blog</Button>
          </Space>
        </Space>
      ) : (
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <Title level={4} style={{ margin: 0 }}>Quản lý nội dung và SEO</Title>
          <Space>
            <Button icon={<GlobalOutlined />} onClick={openWebsite}>Xem website</Button>
            <Button icon={<LinkOutlined />} onClick={openSitemap}>Xem sitemap</Button>
            <Button icon={<CopyOutlined />} onClick={copySitemapLink}>Copy link sitemap</Button>
            <Button icon={<DownloadOutlined />} onClick={downloadSitemap}>Tải sitemap</Button>
            <Button loading={pingingGoogleSitemap} onClick={pingGoogleSitemap}>Ping Google</Button>
            <Button icon={<ReloadOutlined />} onClick={reload}>Làm mới</Button>
            <Button icon={<RobotOutlined />} onClick={openBulkAIDrawer}>Tạo bài AI hàng loạt</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={createPost}>Tạo blog</Button>
          </Space>
        </Space>
      )}

      <Tabs
        items={[
          {
            key: "posts",
            label: "Blog",
            children: (
              <Card>
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Row gutter={12}>
                    <Col xs={24} md={12}>
                      <Input.Search
                        placeholder="Tìm theo tiêu đề/slug"
                        defaultValue={postFilters.info}
                        allowClear
                        onSearch={(value) => onPostSearch({ info: value })}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Select
                        style={{ width: "100%" }}
                        allowClear
                        placeholder="Lọc theo trạng thái"
                        value={postFilters.status}
                        options={statusOptions}
                        onChange={(value) => onPostSearch({ status: value })}
                      />
                    </Col>
                  </Row>

                  <Space direction={isMobile ? "vertical" : "horizontal"} style={{ width: "100%" }}>
                    <Text type="secondary">Đã chọn: {selectedPostIDs.length} bài viết</Text>
                    <Select
                      style={{ width: isMobile ? "100%" : 220 }}
                      value={bulkPostStatus}
                      options={statusOptions}
                      onChange={setBulkPostStatus}
                    />
                    <Button
                      type="primary"
                      loading={bulkUpdatingStatus}
                      disabled={selectedPostIDs.length === 0}
                      onClick={updateSelectedPostsStatus}
                    >
                      Cập nhật trạng thái hàng loạt
                    </Button>
                    <Button
                      disabled={selectedPostIDs.length === 0}
                      onClick={() => setSelectedPostIDs([])}
                    >
                      Bỏ chọn
                    </Button>
                  </Space>

                  {isMobile ? (
                    <List
                      loading={loading}
                      dataSource={posts}
                      renderItem={(record) => renderMobilePostCard(record)}
                      locale={{ emptyText: "Chưa có blog" }}
                      pagination={{
                        current: postPaging.page,
                        pageSize: postPaging.page_size,
                        total: totalPosts,
                        onChange: onPostPageChange,
                        size: "small",
                        align: "center",
                      }}
                    />
                  ) : (
                    <Table
                      rowKey="id"
                      loading={loading}
                      columns={postColumns}
                      dataSource={posts}
                      rowSelection={{
                        selectedRowKeys: selectedPostIDs,
                        onChange: (keys) => setSelectedPostIDs(keys),
                      }}
                      pagination={{
                        current: postPaging.page,
                        pageSize: postPaging.page_size,
                        total: totalPosts,
                        onChange: onPostPageChange,
                      }}
                    />
                  )}
                </Space>
              </Card>
            ),
          },
          {
            key: "categories",
            label: "Danh mục",
            children: (
              <Card>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      placeholder="Nhập tên danh mục"
                    />
                    <Button loading={savingCategory} type="primary" onClick={saveCategory}>Thêm</Button>
                  </Space.Compact>
                  <Table rowKey="id" columns={categoryColumns} dataSource={categories} pagination={false} />
                </Space>
              </Card>
            ),
          },
          {
            key: "tags",
            label: "Tag",
            children: (
              <Card>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      value={tagName}
                      onChange={(e) => setTagName(e.target.value)}
                      placeholder="Nhập tên thẻ"
                    />
                    <Button loading={savingTag} type="primary" onClick={saveTag}>Thêm</Button>
                  </Space.Compact>
                  <Table rowKey="id" columns={tagColumns} dataSource={tags} pagination={false} />
                </Space>
              </Card>
            ),
          },
          {
            key: "brand-profile",
            label: "Giới thiệu thương hiệu",
            children: (
              <Card loading={brandLoading}>
                <Form form={brandForm} layout="vertical">
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="brand_name" label="Tên thương hiệu" rules={[{ required: true, message: "Nhập tên thương hiệu" }]}>
                        <Input placeholder="Ví dụ: Sipro" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="slogan" label="Slogan">
                        <Input placeholder="Ví dụ: Chuyển đổi số bán sỉ đơn giản" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="full_description"
                    label="Mô tả thương hiệu tổng quan"
                    rules={[{ required: true, message: "Nhập mô tả thương hiệu" }]}
                    extra="Đây là nguồn ngữ cảnh chính để các chức năng AI sử dụng về sau."
                  >
                    <TextArea rows={8} placeholder="Mô tả đầy đủ về thương hiệu, sứ mệnh, mô hình vận hành, điểm khác biệt..." />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="value_proposition" label="Giá trị cốt lõi / Điểm khác biệt">
                        <TextArea rows={4} placeholder="Nêu rõ lợi thế cạnh tranh và giá trị mang lại cho chủ shop bán sỉ" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="target_customer" label="Chân dung khách hàng mục tiêu">
                        <TextArea rows={4} placeholder="Ví dụ: chủ shop bán sỉ, đại lý, nhà phân phối..." />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="products_and_strengths" label="Sản phẩm/dịch vụ trọng tâm">
                        <TextArea rows={4} placeholder="Các nhóm chức năng mạnh của nền tảng và lợi ích vận hành" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="tone_of_voice" label="Giọng điệu thương hiệu">
                        <TextArea rows={4} placeholder="Ví dụ: chuyên nghiệp, gần gũi, thực chiến, ngắn gọn" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="ai_context_note" label="Lưu ý cho AI khi sử dụng mô tả">
                    <TextArea rows={3} placeholder="Ví dụ: tránh cam kết quá mức, luôn nhấn mạnh chuyển đổi từ chốt đơn thủ công sang hệ thống chuyên nghiệp" />
                  </Form.Item>

                  <Form.Item name="keywords" label="Từ khóa thương hiệu">
                    <Select mode="tags" tokenSeparators={[","]} placeholder="Nhập các từ khóa thương hiệu phục vụ AI và SEO" />
                  </Form.Item>

                  <Space>
                    <Button type="primary" loading={brandSaving} onClick={saveBrandProfile}>Lưu hồ sơ thương hiệu</Button>
                    <Button onClick={reload}>Tải lại</Button>
                  </Space>
                </Form>
              </Card>
            ),
          },
        ]}
      />

      <PostEditorDrawer
        isMobile={isMobile}
        editorOpen={editorOpen}
        editingPost={editingPost}
        editorLoading={editorLoading}
        revisionLoading={revisionLoading}
        revisions={revisions}
        postForm={postForm}
        closeEditor={closeEditor}
        savePost={savePost}
        checkSlug={checkSlug}
        openPostPreview={openPostPreview}
        aiGenerating={aiGenerating}
        generatePostHTMLByAI={generatePostHTMLByAI}
        aiFacebookGenerating={aiFacebookGenerating}
        aiFacebookContent={aiFacebookContent}
        setAIFacebookContent={setAIFacebookContent}
        generateFacebookPostByAI={generateFacebookPostByAI}
        copyFacebookPostContent={copyFacebookPostContent}
        aiImageGenerating={aiImageGenerating}
        aiImagePrompt={aiImagePrompt}
        setAIImagePrompt={setAIImagePrompt}
        aiGeneratedImage={aiGeneratedImage}
        setAIGeneratedImage={setAIGeneratedImage}
        generatePostImageByAI={generatePostImageByAI}
        applyGeneratedImageAsFeatured={applyGeneratedImageAsFeatured}
        appendGeneratedImageToGallery={appendGeneratedImageToGallery}
        onEditorStatusChange={onEditorStatusChange}
        categoryOptions={categoryOptions}
        tagOptions={tagOptions}
        restoreRevision={restoreRevision}
      />

      <BulkAIPostsDrawer
        open={bulkAIOpen}
        onClose={closeBulkAIDrawer}
        topic={bulkAITopic}
        setTopic={setBulkAITopic}
        count={bulkAICount}
        setCount={setBulkAICount}
        topics={bulkAITopics}
        selectedIDs={bulkAISelectedIDs}
        setSelectedIDs={setBulkAISelectedIDs}
        generatingTopics={bulkAIGeneratingTopics}
        creatingPosts={bulkAICreatingPosts}
        onGenerateTopics={generateTopicListByAI}
        onCreatePosts={createSelectedPostsByAI}
        isMobile={isMobile}
      />

      {isMobile ? (
        <FloatButton
          type="primary"
          icon={<PlusOutlined />}
          tooltip="Tạo blog"
          onClick={createPost}
        />
      ) : null}
    </Space>
  );
};

export default Desktop;
